import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, ArrowLeft, Link2, Copy, CheckCircle2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

function AssessmentQuestions() {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    order: 1,
    prompt: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    map_a: 'D',
    map_b: 'I',
    map_c: 'S',
    map_d: 'C',
  });

  useEffect(() => {
    fetchAssessment();
    fetchEvaluations();
  }, [assessmentId]);

  const fetchAssessment = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/assessments/${assessmentId}`);
      setAssessment(response.data);
      
      // Set next order number
      if (response.data.questions?.length > 0) {
        const maxOrder = Math.max(...response.data.questions.map(q => q.order));
        setNewQuestion(prev => ({ ...prev, order: maxOrder + 1 }));
      }
    } catch (error) {
      toast.error('Erro ao carregar avaliação');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluations = async () => {
    try {
      const response = await api.get('/evaluations');
      // Filter evaluations that use this assessment
      const filtered = response.data.filter(e => e.assessment_id === assessmentId);
      setEvaluations(filtered);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
    }
  };

  const handleAddQuestion = async () => {
    // Validate
    if (!newQuestion.prompt.trim()) {
      toast.error('Pergunta é obrigatória');
      return;
    }
    if (!newQuestion.option_a.trim() || !newQuestion.option_b.trim() || 
        !newQuestion.option_c.trim() || !newQuestion.option_d.trim()) {
      toast.error('Todas as alternativas são obrigatórias');
      return;
    }

    try {
      await api.post(`/assessments/${assessmentId}/questions`, newQuestion);
      toast.success('Pergunta adicionada com sucesso!');
      setOpenCreate(false);
      setNewQuestion({
        order: newQuestion.order + 1,
        prompt: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        map_a: 'D',
        map_b: 'I',
        map_c: 'S',
        map_d: 'C',
      });
      fetchAssessment();
    } catch (error) {
      toast.error('Erro ao adicionar pergunta');
      console.error(error);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pergunta?')) {
      return;
    }

    try {
      await api.delete(`/assessments/${assessmentId}/questions/${questionId}`);
      toast.success('Pergunta excluída com sucesso!');
      fetchAssessment();
    } catch (error) {
      toast.error('Erro ao excluir pergunta');
      console.error(error);
    }
  };

  const copyToClipboard = (publicLink, evalId) => {
    const fullUrl = `${window.location.origin}/public/register/${publicLink}`;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl)
        .then(() => {
          toast.success('Link copiado!');
          setCopiedLink(evalId);
          setTimeout(() => setCopiedLink(null), 2000);
        })
        .catch(() => {
          fallbackCopyToClipboard(fullUrl, evalId);
        });
    } else {
      fallbackCopyToClipboard(fullUrl, evalId);
    }
  };

  const fallbackCopyToClipboard = (text, evalId) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Link copiado!');
      setCopiedLink(evalId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error('Erro ao copiar. Por favor, copie manualmente.');
    }
    
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <p className="text-gray-600">Carregando...</p>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <p className="text-gray-600">Avaliação não encontrada</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/assessments')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{assessment.name}</h1>
              <p className="text-gray-600">{assessment.description || 'Gerenciar perguntas desta avaliação'}</p>
            </div>
          </div>

          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" />
                Nova Pergunta
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Adicionar Nova Pergunta</DialogTitle>
                <DialogDescription>
                  Preencha a pergunta e as alternativas com seus respectivos mapeamentos DISC
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="order">Ordem</Label>
                  <Input
                    id="order"
                    type="number"
                    value={newQuestion.order}
                    onChange={(e) => setNewQuestion({ ...newQuestion, order: parseInt(e.target.value) })}
                  />
                </div>

                <div>
                  <Label htmlFor="prompt">Pergunta *</Label>
                  <Input
                    id="prompt"
                    placeholder="Digite a pergunta..."
                    value={newQuestion.prompt}
                    onChange={(e) => setNewQuestion({ ...newQuestion, prompt: e.target.value })}
                  />
                </div>

                {/* Option A */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3">
                    <Label htmlFor="option_a">Alternativa A *</Label>
                    <Input
                      id="option_a"
                      placeholder="Alternativa A"
                      value={newQuestion.option_a}
                      onChange={(e) => setNewQuestion({ ...newQuestion, option_a: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>DISC A</Label>
                    <Select value={newQuestion.map_a} onValueChange={(val) => setNewQuestion({ ...newQuestion, map_a: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Option B */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3">
                    <Label htmlFor="option_b">Alternativa B *</Label>
                    <Input
                      id="option_b"
                      placeholder="Alternativa B"
                      value={newQuestion.option_b}
                      onChange={(e) => setNewQuestion({ ...newQuestion, option_b: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>DISC B</Label>
                    <Select value={newQuestion.map_b} onValueChange={(val) => setNewQuestion({ ...newQuestion, map_b: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Option C */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3">
                    <Label htmlFor="option_c">Alternativa C *</Label>
                    <Input
                      id="option_c"
                      placeholder="Alternativa C"
                      value={newQuestion.option_c}
                      onChange={(e) => setNewQuestion({ ...newQuestion, option_c: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>DISC C</Label>
                    <Select value={newQuestion.map_c} onValueChange={(val) => setNewQuestion({ ...newQuestion, map_c: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Option D */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3">
                    <Label htmlFor="option_d">Alternativa D *</Label>
                    <Input
                      id="option_d"
                      placeholder="Alternativa D"
                      value={newQuestion.option_d}
                      onChange={(e) => setNewQuestion({ ...newQuestion, option_d: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>DISC D</Label>
                    <Select value={newQuestion.map_d} onValueChange={(val) => setNewQuestion({ ...newQuestion, map_d: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setOpenCreate(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleAddQuestion} className="bg-emerald-600 hover:bg-emerald-700">
                    Adicionar Pergunta
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Evaluations Links Section */}
        {evaluations.length > 0 && (
          <Card className="mb-6 border-l-4 border-emerald-600">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Link2 className="w-5 h-5 mr-2 text-emerald-600" />
                Links Públicos para esta Avaliação
              </CardTitle>
              <CardDescription>
                Compartilhe estes links para que candidatos possam se registrar usando esta avaliação
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {evaluations.map((evaluation) => (
                  <div key={evaluation.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{evaluation.name}</span>
                      {evaluation.description && (
                        <span className="text-xs text-gray-500">{evaluation.description}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={`${window.location.origin}/public/register/${evaluation.public_link}`}
                        readOnly
                        className="font-mono text-sm bg-white"
                      />
                      <Button
                        onClick={() => copyToClipboard(evaluation.public_link, evaluation.id)}
                        className={`${
                          copiedLink === evaluation.id
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                      >
                        {copiedLink === evaluation.id ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Copiado!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions List */}
        {assessment.questions?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 mb-4">Nenhuma pergunta cadastrada</p>
              <Button onClick={() => setOpenCreate(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Primeira Pergunta
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {assessment.questions
              ?.sort((a, b) => a.order - b.order)
              .map((question, index) => (
                <Card key={question.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          {question.order}. {question.prompt}
                        </CardTitle>
                        <div className="mt-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">A)</span>
                            <span className="text-sm">{question.option_a}</span>
                            <span className="ml-auto px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {question.map_a}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">B)</span>
                            <span className="text-sm">{question.option_b}</span>
                            <span className="ml-auto px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {question.map_b}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">C)</span>
                            <span className="text-sm">{question.option_c}</span>
                            <span className="ml-auto px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                              {question.map_c}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">D)</span>
                            <span className="text-sm">{question.option_d}</span>
                            <span className="ml-auto px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                              {question.map_d}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteQuestion(question.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                </Card>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AssessmentQuestions;
